import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import type { PresentationContext } from '../../../presentation/types';
import { ECO, layoutGroups } from '../../../components/ecosystemMap';
import { brandLogo } from '../../../components/brandLogo';

/**
 * The finale converges on a single connection rule beneath the official Moderta logo. (An earlier
 * hexagon "mark" was retired: it read as an invented logo, which the brand rules do not allow.)
 * The rule is split into one segment per slot; each enabled group's line claims one, and unclaimed
 * segments are drawn directly so the rule is always complete.
 */
const RULE = { x1: 560, x2: 1040, y: 560, gap: 10 };
const SLOTS = 6;
const segment = (slot: number) => {
  const w = (RULE.x2 - RULE.x1 - RULE.gap * (SLOTS - 1)) / SLOTS;
  const x1 = RULE.x1 + slot * (w + RULE.gap);
  return { x1, y1: RULE.y, x2: x1 + w, y2: RULE.y };
};

/**
 * Each enabled group's line, first as its spoke of the ecosystem, then as one segment of the rule,
 * in portfolio order from left to right.
 */
function buildLines(context: PresentationContext) {
  const layout = layoutGroups(context.groups);
  const slots = Array.from({ length: SLOTS }, (_, i) => i);
  const offset = Math.max(0, Math.floor((SLOTS - context.groups.length) / 2));
  const claimed = new Set<number>();
  const lines = context.groups.map((group, i) => {
    const anchor = layout.get(group.id)!;
    const slot = Math.min(SLOTS - 1, i + offset);
    claimed.add(slot);
    const dir = Math.atan2(anchor.y - ECO.cy, anchor.x - ECO.cx);
    return {
      id: group.id,
      name: group.name,
      color: group.color,
      anchor,
      spoke: {
        x1: ECO.cx + Math.cos(dir) * 110, y1: ECO.cy + Math.sin(dir) * 110,
        x2: anchor.x - Math.cos(dir) * 36, y2: anchor.y - Math.sin(dir) * 36,
      },
      edge: segment(slot),
    };
  });
  return { lines, unclaimed: slots.filter((n) => !claimed.has(n)).map(segment) };
}

/**
 * 0 The product worlds fade until only their lines remain
 * 1 The lines converge into one connection rule
 * 2 The Moderta logo resolves above it
 * 3 End card — Ideas → Systems → Impact. Hold.
 */
export class ClosingScene extends BaseScene {
  public readonly stepCount = 4;
  private readonly mark = buildLines(this.context);

  protected template(): string {
    const lines = this.mark.lines.map((l) => `<line class="cl-line" data-group="${l.id}" style="--family:${l.color}" x1="${l.spoke.x1}" y1="${l.spoke.y1}" x2="${l.spoke.x2}" y2="${l.spoke.y2}"/>`).join('');
    const unclaimed = this.mark.unclaimed.map((e) => `<line class="cl-edge" x1="${e.x1}" y1="${e.y1}" x2="${e.x2}" y2="${e.y2}"/>`).join('');
    const names = this.mark.lines.map((l) => `
      <g class="cl-world" data-group="${l.id}" style="--family:${l.color}">
        <circle cx="${l.anchor.x}" cy="${l.anchor.y}" r="7"/>
        <text x="${l.anchor.x}" y="${l.anchor.y + (l.anchor.y < ECO.cy ? -24 : 38)}" text-anchor="middle">${l.name.toUpperCase()}</text>
      </g>`).join('');

    return `
      <div class="scene-layout closing">
        <svg class="diagram cl-diagram" viewBox="0 0 1600 900" aria-hidden="true">
          <circle class="cl-core" cx="${ECO.cx}" cy="${ECO.cy}" r="6"/>
          ${names}
          <g class="cl-lines">${lines}</g>
          <g class="cl-mark" data-from="1">${unclaimed}</g>
        </svg>
        <div class="cl-copy">
          <h1 class="cl-wordmark" data-from="2">${brandLogo('moderta', { height: 120 })}</h1>
          <div class="cl-end" data-from="3" data-end-anchor>
            <p class="opening-formula" aria-label="Ideas, systems, impact"><span>Ideas</span><i aria-hidden="true"></i><span>Systems</span><i aria-hidden="true"></i><span>Impact</span></p>
          </div>
        </div>
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    // Products leave one at a time; their connections stay behind.
    const tl = this.timeline();
    gsap.set(this.$$('.cl-world'), { opacity: 1 });
    gsap.set(this.$$('.cl-line'), { opacity: 0.35 });
    this.$$('.cl-world').forEach((world, i) => {
      tl.to(world, { opacity: 0, duration: 1, ease: 'power2.inOut' }, 1.2 + i * 0.45);
      tl.to(this.$(`.cl-line[data-group="${this.mark.lines[i].id}"]`), { opacity: 1, duration: 0.8 }, 1.2 + i * 0.45);
    });
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode('closing', step, immediate);

    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();

    gsap.set(this.$$('.cl-world'), { opacity: 0 });
    gsap.to(this.$('.cl-core'), { opacity: step >= 1 ? 0 : 1, duration: d * 0.6 });

    // The lines swing together into the mark, one edge per family.
    const converged = step >= 1;
    this.$$('.cl-line').forEach((line, i) => {
      const target = converged ? this.mark.lines[i].edge : this.mark.lines[i].spoke;
      gsap.to(line, {
        attr: target,
        opacity: 1,
        duration: d * 1.8,
        delay: forward && step === 1 ? i * 0.08 : 0,
        ease: 'expo.inOut',
        overwrite: 'auto',
      });
    });
    this.$('.cl-diagram').dataset.mark = String(converged);
    // The logo rises out of the rule: a mask opening upward, so the line reads as its foundation.
    const logo = this.$('.cl-wordmark .brand-logo');
    if (forward && step === 2) {
      tl.fromTo(logo, { opacity: 0, clipPath: 'inset(100% 0 0 0)', y: 24 }, { opacity: 1, clipPath: 'inset(0% 0 0 0)', y: 0, duration: 1.4, ease: 'expo.out' }, 0.15);
    } else {
      gsap.set(logo, { opacity: 1, clipPath: 'inset(0% 0 0 0)', y: 0 });
    }

    const formula = this.$$('.cl-end .opening-formula span, .cl-end .opening-formula i');
    if (forward && step === 3) tl.fromTo(formula, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.8, stagger: 0.25, ease: 'power3.out' }, 0.6);
    else gsap.set(formula, { opacity: 1, y: 0 });
  }
}
